import { forwardRef, useEffect } from "react";
import {
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setDialog,
  resetForm,
  PointOfSales,
} from "../../../app/slices/posSlice";
import { Controller, useForm } from "react-hook-form";
import { SelectGroup } from "../../../components/Selectors/SelectGroup";
import { SelectProvince } from "../../../components/Selectors/SelectProvince";
import { useState } from "react";
import { SelectRepresentative } from "../../../components/Selectors/SelectRepresentative";
import { NumericFormat } from "react-number-format";
import { SelectCity } from "../../../components/Selectors/SelectCity";
import {
  setCityTable,
  setSelectedProvince,
} from "../../../app/slices/provincesSlice";
import { IoLocation } from "react-icons/io5";
import { SelectPosType } from "../../../components/Selectors/SelectPosType";
import { SelectPosField } from "../../../components/Selectors/SelectPosField";
import { SelectJGroup } from "../../../components/Selectors/jomla/SelectJGroup";
import { SelectJRep } from "../../../components/Selectors/SelectJRep";
import {
  HiOutlineBuildingStorefront,
  HiOutlineUserCircle,
  HiOutlineIdentification,
  HiOutlineRectangleGroup,
  HiOutlineMapPin,
  HiOutlineTag,
  HiOutlineBanknotes,
  HiOutlineBell,
  HiOutlineCog6Tooth,
  HiOutlineDocumentDuplicate,
} from "react-icons/hi2";
import DocumentsSection, { POS_DOCUMENTS } from "./documentsSection";
import FormDialog, {
  SectionCard,
  SwitchCard,
} from "../../../components/FormDialog";
import { BRAND } from "../../../components/MobileList";

const DOCUMENT_NAMES = POS_DOCUMENTS.map((doc) => doc.name);

// The documents go to `pos-image/{pos_id}` as multipart, and only the slots the
// user actually touched are sent — untouched ones keep whatever is on the server.
const buildDocumentsFormData = (data) => {
  const formData = new FormData();
  let hasFile = false;
  DOCUMENT_NAMES.forEach((key) => {
    if (data[key] instanceof File) {
      formData.append(key, data[key]);
      hasFile = true;
    }
  });
  return hasFile ? formData : null;
};

const PosForm = (props) => {
  const _PointOfsales = useSelector((state) => state.pointOfsales.pos);
  const _SearchBody = useSelector((state) => state.pointOfsales.searchBody);
  const _Province = useSelector((state) => state.provinces.provinces);
  const _Group = useSelector((state) => state.group.groupsList);
  const _User = useSelector((state) => state.user.userInfo);
  const _PosType = useSelector((state) => state.posType.posTypes.data);
  const _PosField = useSelector((state) => state.posField.posFields.data);
  const _JGroup = useSelector((state) => state.jGroup.jGroupList);
  const _JRep = useSelector((state) => state.user.jRepList.data);

  const [canTransfer, setCanTransfer] = useState(
    _PointOfsales.form.can_transfer || false,
  );
  const isAdd = _PointOfsales.form.id ? false : true;
  const {
    formState: { errors },
    control,
    watch,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: _PointOfsales.form,
  });

  const isRepresentative = _User.Roles[0].name === "representative";
  const isTeamLeader = _User.Roles[0].name === "team_leader";

  // Documents are kept in the form state like any other field: a saved one is
  // the URL that came back from the API, a freshly picked one is a File. The
  // stored URLs are read from the store rather than the form copy, so `setValue`
  // never loses them behind a new pick and a delete clears them right away.
  const savedDocuments = DOCUMENT_NAMES.reduce(
    (acc, key) => ({ ...acc, [key]: _PointOfsales.form[key] ?? null }),
    {},
  );
  const documentValues = watch(DOCUMENT_NAMES);
  const documents = DOCUMENT_NAMES.reduce(
    (acc, key, index) => ({ ...acc, [key]: documentValues[index] }),
    {},
  );
  const [deletingDocument, setDeletingDocument] = useState(null);

  const handleDeleteDocument = async (name) => {
    setDeletingDocument(name);
    try {
      await dispatch(PointOfSales.deleteImage(_PointOfsales.form.id, name));
      setValue(name, null);
    } catch {
      // The thunk already reported the failure through a notification.
    } finally {
      setDeletingDocument(null);
    }
  };

  const NumericFormatCustom = forwardRef(
    function NumericFormatCustom(props, ref) {
      const { onChange, ...other } = props;

      return (
        <NumericFormat
          {...other}
          getInputRef={ref}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value,
              },
            });
          }}
          thousandSeparator
          valueIsNumericString
        />
      );
    },
  );

  // team_leader can't set the financial limits, so they are left out of the payload.
  const financialLimits = (data) =>
    isTeamLeader
      ? {}
      : {
          max_balance: data.max_balance,
          max_debt: data.max_debt,
          daily_transfer_balance: data.daily_transfer_balance,
        };

  const onSubmit = (data) => {
    const documentsFormData = buildDocumentsFormData(data);
    if (isAdd) {
      dispatch(
        PointOfSales.create(
          {
            user_name: data.user_name,
            password: data.password,
            email: data.email,
            mobile: data.mobile,
            name: data.name,
            address: data.address,
            is_active: data.is_active,
            owner: data.owner,
            group_id: data.group_id.id,
            can_transfer: data.can_transfer,
            province_id: data.province_id.id,
            wage: canTransfer ? data.wage : 1,
            max_daily_transfer: canTransfer ? data.max_daily_transfer : 0,
            max_monthly_transfer: canTransfer ? data.max_monthly_transfer : 0,
            min_balance: data.min_balance,
            ...financialLimits(data),
            city_id: data.city_id.id,
            rep_id: data.rep_id?.id ?? null,
            e_payment_rep_id: data.e_payment_rep_id?.id ?? null,
            j_rep_id: data.j_rep_id ?? null,
            is_approved: data.is_approved ? 1 : 0,
            latitude: data.latitude,
            longitude: data.longitude,
            has_card_issuing: data.has_card_issuing,
            has_e_payment: data.has_e_payment,
            sim_card_number: data.sim_card_number,
            pos_type_id: data.pos_type_id,
            pos_field_id: data.pos_field_id,
            j_group_id: data.j_group_id,
            other_id: data.other_id ?? "",
            lock_after_payment: data.lock_after_payment,
          },
          _SearchBody,
          documentsFormData,
        ),
      );
    } else {
      dispatch(
        PointOfSales.update(
          data.id,
          {
            name: data.name,
            owner: data.owner,
            mobile: data.mobile,
            group_id: data.group_id.id,
            can_transfer: data.can_transfer,
            province_id: data.province_id.id,
            address: data.address,
            wage: data.wage,
            max_daily_transfer: data.max_daily_transfer,
            max_monthly_transfer: data.max_monthly_transfer,
            user_name: data.user_name,
            min_balance: data.min_balance,
            ...financialLimits(data),
            e_payment_rep_id: data.e_payment_rep_id?.id ?? null,
            j_rep_id: data.j_rep_id ?? null,
            city_id: data.city_id.id,
            is_approved: data.is_approved ? 1 : 0,
            latitude: data.latitude,
            longitude: data.longitude,
            has_card_recharge: data.has_card_recharge,
            has_card_issuing: data.has_card_issuing,
            has_e_payment: data.has_e_payment,
            sim_card_number: data.sim_card_number,
            pos_type_id: data.pos_type_id,
            pos_field_id: data.pos_field_id,
            j_group_id: data.j_group_id,
            other_id: data.other_id ?? "",
            lock_after_payment: data.lock_after_payment,
            rep_id: data.rep_id?.id ?? null,
          },
          _SearchBody,
          props.inNeedReload,
          props.from,
          props.jOrderSearchBody,
          props.jOrderId,
          documentsFormData,
        ),
      );
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canTransfer === false) {
      setValue("max_monthly_transfer", 0);
      setValue("max_daily_transfer", 0);
      setValue("wage", 0);
    } else {
      setValue("max_monthly_transfer", 5000000);
      setValue("max_daily_transfer", 1000000);
      setValue("wage", 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTransfer]);

  useEffect(() => {
    const currentJGroup = watch("j_group_id");
    if (
      !isRepresentative &&
      (currentJGroup === null || currentJGroup === undefined) &&
      _JGroup.length > 0
    ) {
      setValue("j_group_id", _JGroup[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_JGroup]);

  const dispatch = useDispatch();

  const handleDialog = () => {
    dispatch(setDialog());
  };

  useEffect(() => {
    const selectedCities = _Province.data?.filter((x) =>
      x.id === watch("province_id")?.id ? x.city : null,
    )[0];
    dispatch(setCityTable(selectedCities?.city));
    dispatch(
      setSelectedProvince({
        id: watch("province_id") ? watch("province_id").id : null,
        name: watch("province_id") ? watch("province_id").name : "",
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Province, watch("province_id")]);

  useEffect(() => {
    if (watch("group_id") == null) setValue("group_id", _Group?.data[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Province]);

  return (
    <FormDialog
      open={true}
      onClose={handleDialog}
      maxWidth="md"
      icon={<HiOutlineBuildingStorefront />}
      title={isAdd ? "إضافة نقطة بيع جديدة" : "تعديل نقطة البيع"}
      subtitle={
        isAdd
          ? "أدخل معلومات نقطة البيع لإنشائها"
          : "قم بتحديث معلومات نقطة البيع ثم احفظ التغييرات"
      }
      onSubmit={handleSubmit(onSubmit)}
      saving={_PointOfsales.loading}
      savePermission={{
        permission: "pos_edit",
        role: ["super_admin", "admin"],
      }}
    >
            {/* Account (add mode only) */}
            {isAdd && (
              <SectionCard
                icon={<HiOutlineUserCircle />}
                title="معلومات الحساب"
              >
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="user_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="اسم المستخدم"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="كلمة المرور"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        size="small"
                        label="البريد الالكتروني"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Basic info */}
            <SectionCard
              icon={<HiOutlineIdentification />}
              title="المعلومات الأساسية"
            >
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      required
                      size="small"
                      label="الاسم"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="other_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="معرف النقطة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="owner"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      required
                      size="small"
                      label="المالك"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mobile"
                  rules={{
                    required: { value: true, message: "الحقل مطلوب" },
                    pattern: {
                      value: /^(?:\+964|00964|07[5798])[0-9]{8}$/,
                      message: "رقم الهاتف غير صحيح",
                    },
                  }}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      label="رقم الهاتف"
                      fullWidth
                      error={!!errors.mobile}
                      helperText={errors.mobile ? errors.mobile.message : ""}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sim_card_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="رقم الشريحة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* Classification & groups */}
            {!isRepresentative && (
              <SectionCard
                icon={<HiOutlineRectangleGroup />}
                title="التصنيف والمجاميع"
              >
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectRepresentative
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue("rep_id", newValue ? newValue : null);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="e_payment_rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectRepresentative
                        label="مندوب الدفع الالكتروني"
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue(
                            "e_payment_rep_id",
                            newValue ? newValue : null,
                          );
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="j_rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectJRep
                        onChange={(e, newValue) => {
                          setValue("j_rep_id", newValue ? newValue.id : null);
                        }}
                        value={
                          _JRep.filter((x) => x.id === field.value)[0] ?? null
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="group_id"
                    control={control}
                    render={({ field }) => (
                      <SelectGroup
                        required
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue("group_id", newValue);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="j_group_id"
                    control={control}
                    render={({ field }) => (
                      <SelectJGroup
                        required
                        onChange={(e, newValue) => {
                          setValue(
                            "j_group_id",
                            newValue ? newValue?.id : null,
                          );
                        }}
                        value={
                          _JGroup.filter((x) => x.id === field.value)[0] ?? null
                        }
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Location */}
            <SectionCard icon={<HiOutlineMapPin />} title="الموقع">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="province_id"
                  control={control}
                  render={({ field }) => (
                    <SelectProvince
                      required
                      value={field.value}
                      onChange={(e, newValue) => {
                        setValue("province_id", newValue ? newValue : null);
                        setValue("city_id", null);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <SelectCity
                      {...field}
                      required
                      value={field.value}
                      onChange={(e, newValue) => {
                        setValue("city_id", newValue ? newValue : null);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="اقرب نقطة دالة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="latitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="خط العرض (Latitude)"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="longitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="خط الطول (Longitude)"
                      fullWidth
                      {...field}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="عرض الموقع على الخريطة">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  sx={{ color: BRAND }}
                                  disabled={
                                    !watch("longitude") || !watch("latitude")
                                  }
                                  onClick={() => {
                                    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${watch("latitude")},${watch("longitude")}`;
                                    window.open(googleMapsUrl, "_blank");
                                  }}
                                >
                                  <IoLocation />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* POS type */}
            <SectionCard icon={<HiOutlineTag />} title="نوع النقطة">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="pos_type_id"
                  control={control}
                  render={({ field }) => (
                    <SelectPosType
                      onChange={(e, newValue) => {
                        setValue("pos_type_id", newValue ? newValue.id : null);
                      }}
                      value={
                        _PosType.filter((x) => x.id === field.value)[0] ?? null
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="pos_field_id"
                  control={control}
                  render={({ field }) => (
                    <SelectPosField
                      onChange={(e, newValue) => {
                        setValue("pos_field_id", newValue ? newValue.id : null);
                      }}
                      value={
                        _PosField.filter((x) => x.id === field.value)[0] ?? null
                      }
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* Documents */}
            <SectionCard
              icon={<HiOutlineDocumentDuplicate />}
              title="المستمسكات والصور"
            >
              <Grid item xs={12}>
                <DocumentsSection
                  values={documents}
                  savedValues={savedDocuments}
                  deletingName={deletingDocument}
                  onChange={(name, file) =>
                    setValue(name, file, { shouldDirty: true })
                  }
                  onDelete={isAdd ? undefined : handleDeleteDocument}
                />
                {isAdd && (
                  <Typography
                    sx={{ mt: 1.5, fontSize: 12.5, color: "#667085" }}
                  >
                    ترفع المستمسكات بعد انشاء نقطة البيع مباشرة.
                  </Typography>
                )}
              </Grid>
            </SectionCard>

            {/* Financial limits */}
            {!isTeamLeader && (
              <SectionCard icon={<HiOutlineBanknotes />} title="الحدود المالية">
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="daily_transfer_balance"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="تحويل الرصيد في اليوم"
                        fullWidth
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                {!isRepresentative && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="max_balance"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          required
                          size="small"
                          label="الحد الاعلى للرصيد"
                          fullWidth
                          {...field}
                          InputProps={{
                            inputComponent: NumericFormatCustom,
                            startAdornment: (
                              <InputAdornment position="start">
                                IQD
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                {!isRepresentative && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="max_debt"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          required
                          size="small"
                          label="الحد الاعلى للدين"
                          fullWidth
                          {...field}
                          InputProps={{
                            inputComponent: NumericFormatCustom,
                            startAdornment: (
                              <InputAdornment position="start">
                                IQD
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
              </SectionCard>
            )}

            {/* Balance alerts */}
            {!isRepresentative && (
              <SectionCard icon={<HiOutlineBell />} title="تنبيهات الرصيد">
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="min_balance"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الادنى للرصيد"
                        fullWidth
                        {...field}
                        helperText="للتنبيه فقط - لا يؤثر على عمل النقطة"
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Features & permissions */}
            <SectionCard
              icon={<HiOutlineCog6Tooth />}
              title="الميزات والصلاحيات"
            >
              <Grid item xs={12} sm={6}>
                <Controller
                  name="has_card_issuing"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="اصدار البطاقات" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="has_e_payment"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="الدفع الالكتروني" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="lock_after_payment"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="قفل بعد الدفع" />
                  )}
                />
              </Grid>
              {!isAdd && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="has_card_recharge"
                    control={control}
                    render={({ field }) => (
                      <SwitchCard
                        field={field}
                        label="شحن البطاقات"
                        onChange={(e) => {
                          setValue("has_card_recharge", e.target.checked);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="is_approved"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="المصادقه" />
                  )}
                />
              </Grid>
              {!isRepresentative && (
                <Grid item xs={12}>
                  <Controller
                    name="can_transfer"
                    control={control}
                    render={({ field }) => (
                      <SwitchCard
                        field={field}
                        label="امكانية التحويل"
                        onChange={(e) => {
                          setCanTransfer(e.target.checked);
                          setValue("can_transfer", e.target.checked);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_daily_transfer"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للتحويل في اليوم"
                        fullWidth
                        value={field.value}
                        onChange={(e) =>
                          setValue("max_daily_transfer", e.target.value)
                        }
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_monthly_transfer"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للتحويل في الشهر"
                        fullWidth
                        value={field.value}
                        onChange={(e) =>
                          setValue("max_monthly_transfer", e.target.value)
                        }
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="wage"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="العمولة"
                        fullWidth
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                        }}
                        helperText="1 = 0.001"
                      />
                    )}
                  />
                </Grid>
              )}
            </SectionCard>
    </FormDialog>
  );
};

export default PosForm;
